import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { type FormatOptionLabelMeta } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { AsyncSelect } from '@databases/atlaskit/Select';
import { defaultStoreUtils } from '@databases/redux';
import {
  type BasicDatabase,
  type BrokenDatabase,
  type Database,
  type ShallowDatabase,
} from '@databases/types/database';
import {
  getDatabaseByContentId,
  searchDatabases,
} from '@databases/utils/database/service';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import React, { type FunctionComponent, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getDatabaseIdFromString } from '../databaseDetect';
import { messages } from '../messages';

import DatabasePickerOption from './DatabasePickerOption';
import { ErrorMessage } from './databasePicker.styles';

type Option = ShallowDatabase | BasicDatabase | BrokenDatabase;

export interface DatabasePickerProps {
  selectedDatabase: Database | BrokenDatabase | undefined;
  onChange: (value: Database | BrokenDatabase | undefined) => void;
  isInvalid?: boolean;
}

export const formatOptionLabel = (
  value: Option,
  meta: FormatOptionLabelMeta<Option>
): React.ReactNode => {
  if (meta.context === 'menu') {
    return <DatabasePickerOption value={value} />;
  } else {
    return value.name;
  }
};

const DatabasePicker: FunctionComponent<DatabasePickerProps> = ({
  selectedDatabase,
  onChange,
  isInvalid,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = defaultStoreUtils.useDispatch();

  const intl = useIntl();
  const { formatMessage } = intl;

  const loadOptions = useCallback(
    async (
      inputValue: string
    ): Promise<{ label: string; options: BasicDatabase[] }[] | BasicDatabase[]> => {
      const databaseContentId = getDatabaseIdFromString(inputValue);
      if (databaseContentId) {
        const database = await getDatabaseByContentId(databaseContentId);

        return [database];
      } else {
        const databases: BasicDatabase[] = await searchDatabases(inputValue);

        return [
          {
            label: formatMessage(messages['generic-database-picker.menu-header']),
            options: databases,
          },
        ];
      }
    },
    [formatMessage]
  );

  const handleChange = useCallback(
    async (value: Option | null) => {
      if (!value) {
        onChange(undefined);
        return;
      }

      const initialDb = value as ShallowDatabase;
      setIsLoading(true);
      const finalDb = await dispatch(loadDatabaseByContentId(initialDb.contentId));
      onChange(finalDb);

      setIsLoading(false);
    },
    [dispatch, onChange]
  );

  const getOptionValue = (value: Option) => {
    return value && 'contentId' in value ? value.contentId : '';
  };

  return (
    <>
      <AsyncSelect<Option>
        inputId="database-picker-input"
        value={selectedDatabase ?? null}
        loadOptions={loadOptions}
        formatOptionLabel={formatOptionLabel}
        getOptionValue={getOptionValue}
        onChange={handleChange}
        isLoading={isLoading}
        isClearable={true}
        isInvalid={isInvalid}
        classNamePrefix="database-picker"
        placeholder={formatMessage(
          messages['generic-database-picker.search-database-placeholder']
        )}
      />
      {isInvalid && (
        <ErrorMessage>
          <StatusErrorIcon
            size="small"
            label={formatMessage(messages['generic-database-picker.error-label'])}
            color={token('color.text.danger')}
          />
          {formatMessage(messages['generic-database-picker.error-message'])}
        </ErrorMessage>
      )}
    </>
  );
};

export default DatabasePicker;
